/**
 * Lightweight, deterministic article recommender (no LLM call).
 *  - recommendFromText(): scores a coach message against FR/EN keyword banks per lever.
 *  - recommendFromMetrics(): maps the user's lowest progress metric to a lever.
 *  - weeklyPick(): stable rotation used when there is no data yet.
 */
import { ARTICLE_KEYS } from '../seo/seoConfig.mjs';

const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Each keyword is matched on the accent-stripped, lower-cased text (word-prefix match).
const KEYWORDS = {
  skin: ['peau', 'teint', 'acne', 'hydrat', 'nettoyant', 'creme', 'spf', 'solaire', 'pores', 'sebum', 'cernes', 'skin', 'complexion', 'moisturi', 'cleanser', 'sunscreen', 'dermat'],
  beard: ['barbe', 'bouc', 'moustache', 'rasage', 'raser', 'pilosite', 'ligne de cou', 'beard', 'stubble', 'shav', 'facial hair', 'neckline'],
  hair: ['cheveux', 'coiffure', 'coupe', 'coiffeur', 'barbier', 'degrade', 'frange', 'volume', 'hair', 'haircut', 'hairstyle', 'barber', 'fade', 'fringe'],
  posture: ['posture', 'epaules', 'dos', 'nuque', 'bassin', 'colonne', 'redress', 'se tenir', 'shoulders', 'back', 'neck', 'pelvis', 'spine', 'stand tall', 'slouch'],
  style: ['style', 'tenue', 'vetement', 'vestimentaire', 'garde-robe', 'chemise', 'pantalon', 'chaussure', 'veste', 'couleur', 'coupe ajustee', 'retouche', 'outfit', 'clothing', 'wardrobe', 'shirt', 'trousers', 'shoes', 'jacket', 'colors', 'fit', 'tailor'],
  presence: ['presence', 'regard', 'contact visuel', 'voix', 'gestuelle', 'langage corporel', 'charisme', 'confiance', 'assurance', 'eye contact', 'voice', 'gesture', 'body language', 'charisma', 'confidence'],
  sleep: ['sommeil', 'dormir', 'nuit', 'fatigue', 'energie', 'lumiere du jour', 'hydratation', 'cafeine', 'sleep', 'tired', 'energy', 'daylight', 'caffeine', 'dark circles'],
  routine: ['routine', 'habitude', 'constance', 'regularite', 'discipline', 'rappel', 'hebdomadaire', 'quotidien', 'habit', 'consistency', 'reminder', 'weekly', 'daily', 'checklist'],
  smile: ['sourire', 'dents', 'dentaire', 'dentiste', 'detartrage', 'blanchiment', 'haleine', 'gencive', 'smile', 'teeth', 'tooth', 'dental', 'dentist', 'whitening', 'breath', 'gums'],
  training: ['musculation', 'muscle', 'carrure', 'silhouette', 'entrainement', 'seance', 'force', 'proteine', 'tour de taille', 'masse grasse', 'composition corporelle', 'tractions', 'strength', 'workout', 'gym', 'lifting', 'protein', 'body composition', 'waist', 'physique', 'v shape', 'v-shape'],
  accessories: ['accessoire', 'lunettes', 'montre', 'ceinture', 'chaussures', 'solaires', 'monture', 'accessor', 'glasses', 'eyewear', 'sunglasses', 'watch', 'belt', 'shoes', 'frames'],
  eyebrows: ['sourcil', 'poils du nez', 'oreilles', 'ongles', 'parfum', 'finition', 'pince a epiler', 'eyebrow', 'brow', 'nose hair', 'ear hair', 'nails', 'fragrance', 'cologne', 'perfume'],
  photo: ['photo de profil', 'prendre une photo', 'selfie', 'portrait', 'lumiere naturelle', 'angle', 'objectif', 'photogenique', 'cadrage', 'retardateur', 'profile picture', 'profile photo', 'take a photo', 'camera', 'lighting', 'photogenic', 'headshot', 'framing', 'self-timer'],
};

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Count keyword hits per lever. */
export const scoreText = (text) => {
  const n = normalize(text);
  const scores = {};
  for (const key of ARTICLE_KEYS) {
    let score = 0;
    for (const kw of KEYWORDS[key] || []) {
      const re = new RegExp(`\\b${escapeRe(normalize(kw))}`, 'g');
      const hits = n.match(re);
      if (hits) score += hits.length;
    }
    scores[key] = score;
  }
  return scores;
};

/**
 * Recommend up to `max` articles for a coach message.
 * Requires at least `minScore` keyword hits to avoid noise on short replies.
 */
export const recommendFromText = (text, { max = 2, minScore = 2 } = {}) => {
  if (!text || text.length < 80) return [];
  const scores = scoreText(text);
  return Object.entries(scores)
    .filter(([, s]) => s >= minScore)
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([key]) => key);
};

/** Progress metric -> lever mapping (dashboard "read this week"). */
const METRIC_TO_ARTICLE = {
  fitness: 'training',
  weight_metric: 'training',
  grooming: 'beard',
  style_metric: 'style',
  confidence_metric: 'presence',
};

/**
 * Pick the lever behind the lowest latest metric value (0-100 scale assumed).
 * Returns { key, metric } or null when no mapped metric exists.
 */
export const recommendFromMetrics = (latestProgress = []) => {
  const latestByMetric = {};
  for (const entry of latestProgress) {
    const m = entry?.metric_type;
    if (!METRIC_TO_ARTICLE[m]) continue;
    if (!latestByMetric[m] || new Date(entry.created_at) > new Date(latestByMetric[m].created_at)) latestByMetric[m] = entry;
  }
  const entries = Object.values(latestByMetric);
  if (!entries.length) return null;
  entries.sort((a, b) => Number(a.value) - Number(b.value));
  const lowest = entries[0];
  return { key: METRIC_TO_ARTICLE[lowest.metric_type], metric: lowest.metric_type, value: Number(lowest.value) };
};

/** Deterministic weekly rotation over the article list. */
export const weeklyPick = (date = new Date()) => {
  const start = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.floor((date - start) / (7 * 24 * 3600 * 1000));
  return ARTICLE_KEYS[week % ARTICLE_KEYS.length];
};
